import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface CreditosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (creditsAmount: number) => Promise<void>;
  isLoading: boolean;
}

const creditosOptions = [
  { credits: 1, valor: "R$ 120,00", valorPorVaga: "R$ 120" },
  { credits: 3, valor: "R$ 330,00", valorPorVaga: "R$ 110" },
  { credits: 5, valor: "R$ 500,00", valorPorVaga: "R$ 100" },
  { credits: 10, valor: "R$ 900,00", valorPorVaga: "R$ 90" },
];

export function CreditosModal({ open, onOpenChange, onConfirm, isLoading }: CreditosModalProps) {
  const [selectedCredits, setSelectedCredits] = useState<number>(1);
  const isMobile = useIsMobile();

  const handleConfirm = async () => {
    await onConfirm(selectedCredits);
  };

  const ModalContent = () => (
    <RadioGroup
      value={selectedCredits.toString()}
      onValueChange={(value) => setSelectedCredits(Number(value))}
      className="space-y-3 py-4"
    >
      {creditosOptions.map((option) => (
        <div
          key={option.credits}
          className="flex items-center space-x-3 rounded-lg border p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        >
          <RadioGroupItem value={option.credits.toString()} id={`credits-${option.credits}`} />
          <Label
            htmlFor={`credits-${option.credits}`}
            className="flex-1 cursor-pointer flex justify-between items-center"
          >
            <span className="font-medium">
              {option.credits} crédito{option.credits > 1 ? "s" : ""}
            </span>
            <span className="text-muted-foreground">
              {option.valor} <span className="text-xs">({option.valorPorVaga}/vaga)</span>
            </span>
          </Label>
        </div>
      ))}
    </RadioGroup>
  );

  const ModalActions = () => (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
        Cancelar
      </Button>
      <Button onClick={handleConfirm} disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : (
          "Ir para Pagamento"
        )}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>Comprar Créditos</DrawerTitle>
            <DrawerDescription>
              Selecione a quantidade de créditos que deseja comprar. Validade: 90 dias.
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4">
            <ModalContent />
          </div>
          <DrawerFooter>
            <ModalActions />
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Comprar Créditos</DialogTitle>
          <DialogDescription>
            Selecione a quantidade de créditos que deseja comprar. Validade: 90 dias.
          </DialogDescription>
        </DialogHeader>
        <ModalContent />
        <ModalActions />
      </DialogContent>
    </Dialog>
  );
}
